import type { PostApiV1SelectionsSelectionIdApplicantResume200 } from "@/api/recrutaAI/generated/aISaaSAPI.schemas";
import {
	Badge,
	Box,
	Container,
	Divider,
	Flex,
	Grid,
	Group,
	RingProgress,
	Stack,
	Tabs,
	Text,
	Title,
	useMantineTheme,
} from "@mantine/core";
import { Accordion, Image } from "@mantine/core";

type Props = {
	resume: PostApiV1SelectionsSelectionIdApplicantResume200;
};

export const RecruiteeDetail = ({ resume }: Props) => {
	const theme = useMantineTheme();

	return (
		<Container>
			<Grid mb="lg">
				<Grid.Col span={5.9}>
					<Stack gap={2}>
						<Text size="md" c="gray.7">
							現職
						</Text>
						<Text size="md" c="gray.9">
							{/* MEMO: 配列の一番目が最新の職歴 */}
							{resume.result.careerHistories?.[0]?.company}
						</Text>
					</Stack>
				</Grid.Col>
				<Divider orientation="vertical" />
				<Grid.Col span={5.9}>
					<Stack gap={2} pl="md">
						<Text size="md" c="gray.7">
							選考基準
						</Text>
						<Text size="md" c="gray.9">
							{resume.result.jobInformationName}
						</Text>
					</Stack>
				</Grid.Col>
			</Grid>
			<Tabs defaultValue="aiDocumentSelectionResult">
				<Tabs.List mb="md">
					<Tabs.Tab value="aiDocumentSelectionResult">AI書類選考結果</Tabs.Tab>
					<Tabs.Tab value="resumeAndCareerHistory">履歴書・職務経歴書</Tabs.Tab>
				</Tabs.List>
				<Tabs.Panel value="aiDocumentSelectionResult">
					<Stack gap={24} p="md">
						<Box>
							<Flex direction="row" align="flex-start" gap={24} p="md">
								<Stack gap={8}>
									<Text size="md" c="gray.7">
										総合スコア
									</Text>
									<RingProgress
										size={160}
										thickness={16}
										sections={[
											{
												value: (100 * resume.result.totalScore) / 5,
												color: "primary",
											},
										]}
										label={
											<Text ta="center" fw={700}>
												<span style={{ fontSize: 32 }}>
													{resume.result.totalScore}
												</span>
												<span style={{ fontSize: 16 }}>/5</span>
											</Text>
										}
									/>
								</Stack>
								<Grid bg="gray.0" p="md" style={{ borderRadius: 8 }} w="100%">
									<Accordion
										w="100%"
										chevronSize={20}
										defaultValue={resume.result.scores?.[0]?.title}
										styles={{
											chevron: {
												color: theme.colors.primary[6],
												stroke: theme.colors.primary[6],
											},
										}}
									>
										{resume.result.scores.map((score) => (
											<Accordion.Item key={score.title} value={score.title}>
												<Accordion.Control>
													<Flex
														direction="row"
														align="center"
														justify="space-between"
														mr="sm"
													>
														<Text c="gray.7">{score.title}</Text>
														<Text fw={700}>
															<span style={{ fontSize: 20 }}>
																{score.score}
															</span>
															<span style={{ fontSize: 16 }}>/5</span>
														</Text>
													</Flex>
												</Accordion.Control>
												<Accordion.Panel
													mah={200}
													c="gray.7"
													style={{
														overflow: "hidden",
														overflowY: "scroll",
														whiteSpace: "pre-wrap",
													}}
												>
													{score.rationale}
												</Accordion.Panel>
											</Accordion.Item>
										))}
									</Accordion>
								</Grid>
							</Flex>
							<Text size="md" c="gray.9">
								{resume.result.scoringSummary}
							</Text>
						</Box>
						<Stack>
							<Title order={3}>候補者の特徴</Title>
							<Group gap="xs">
								{resume.result.skills.map((skill) => (
									<Badge key={skill} variant="light">
										{skill}
									</Badge>
								))}
							</Group>
							<Stack bg="gray.0" p="md">
								<Text size="md" c="gray.9">
									{resume.result.careerSummary}
								</Text>
							</Stack>
						</Stack>
						<Stack>
							<Title order={3}>面接で確認したいポイント</Title>
							{resume.result.confirmationMatters.map((item) => (
								<Stack bg="gray.0" p="md" key={item.point}>
									<Text size="lg" c="gray.7">
										{item.point}
									</Text>
									<Text size="md" c="gray.9">
										{item.detail}
									</Text>
								</Stack>
							))}
						</Stack>
					</Stack>
				</Tabs.Panel>

				<Tabs.Panel value="resumeAndCareerHistory">
					<Stack justify="center" align="center" p="md">
						{resume.result.resumePngBase64 ? (
							resume.result.resumePngBase64.startsWith(
								"data:application/pdf;base64,",
							) ? (
								<iframe
									src={resume.result.resumePngBase64}
									title="履歴書・職務経歴書"
									style={{ width: "100%", height: "745px" }}
								/>
							) : (
								<Image
									src={resume.result.resumePngBase64}
									alt="履歴書・職務経歴書"
									w="100%"
									mah={745}
								/>
							)
						) : (
							<Text size="md" style={{ whiteSpace: "pre-wrap" }}>
								{resume.result.resumeText ?? ""}
							</Text>
						)}
					</Stack>
				</Tabs.Panel>
			</Tabs>
		</Container>
	);
};
